import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';

const BASE = '/api';
function api(method, path, body) {
  const token = localStorage.getItem('ozon_token');
  return fetch(`${BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  }).then(async r => {
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || r.statusText);
    return d;
  });
}

function StockUpdateModal({ product, typeKey, typeName, currentStock, onClose, onUpdated }) {
  const [value,       setValue]      = useState(String(currentStock));
  const [saving,      setSaving]     = useState(false);
  const [error,       setError]      = useState('');
  const [warehouses,  setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [loading,     setLoading]    = useState(true);

  useEffect(() => {
    setLoading(true);
    api('GET', `/ozon-stock/warehouse-stocks?offer_id=${encodeURIComponent(product.offer_id)}`)
      .then(stocks => {
        const valid = stocks.filter(s => s.warehouse_id);
        setWarehouses(valid);
        if (!valid.length) return;
        const withStock = valid.find(s => s.free_stock > 0);
        setWarehouseId(String((withStock || valid[0]).warehouse_id));
      })
      .catch(() => {
        api('GET', '/ozon-stock/warehouses').then(list => {
          setWarehouses(list.map(w => ({ ...w, free_stock: null })));
          if (list.length) setWarehouseId(String(list[0].warehouse_id));
        }).catch(() => {});
      })
      .finally(() => setLoading(false));
  }, [product.offer_id]);

  async function save() {
    const qty = parseInt(value);
    if (isNaN(qty) || qty < 0) { setError('Geçerli bir sayı gir'); return; }
    if (!warehouseId) { setError('Depo seçin'); return; }
    setSaving(true); setError('');
    try {
      await api('POST', '/ozon-stock/update', {
        offer_id: product.offer_id, type: typeKey, stock: qty,
        old_stock: currentStock, warehouse_id: parseInt(warehouseId),
      });
      onUpdated(product.offer_id, typeKey, qty);
      onClose();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <h2 className="font-bold text-gray-900 mb-1">Stok Güncelle</h2>
        <p className="text-xs text-gray-500 mb-1 font-mono">{product.offer_id}</p>
        {product.name && <p className="text-xs text-gray-400 mb-1 truncate">{product.name}</p>}
        <p className="text-xs text-blue-500 font-semibold mb-4">{typeName}</p>

        {loading && <p className="text-xs text-gray-400 mb-3">Depolar yükleniyor...</p>}
        {!loading && warehouses.length > 0 && (
          <div className="mb-3">
            <label className="text-xs font-semibold text-gray-500 block mb-1">Depo</label>
            <select value={warehouseId} onChange={e => setWarehouseId(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400">
              {warehouses.map(w => (
                <option key={w.warehouse_id} value={String(w.warehouse_id)}>
                  {w.warehouse_name || w.name}{w.free_stock != null ? ` — ${w.free_stock} adet` : ''}
                </option>
              ))}
            </select>
          </div>
        )}
        {!loading && warehouses.length === 0 && (
          <p className="text-xs text-amber-500 mb-3">Aktif depo bulunamadı</p>
        )}

        <div className="mb-4">
          <label className="text-xs font-semibold text-gray-500 block mb-1">Yeni Stok (mutlak değer)</label>
          <input
            autoFocus type="number" min="0" value={value}
            onChange={e => setValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') onClose(); }}
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-lg font-semibold focus:outline-none focus:border-blue-400 text-center"
          />
          <p className="text-xs text-gray-400 mt-1 text-center">Sipariş edilebilir: <strong>{currentStock}</strong></p>
        </div>
        {error && <p className="text-xs text-red-500 mb-3">{error}</p>}
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 border border-gray-200 text-gray-600 rounded-xl py-2 text-sm hover:bg-gray-50">İptal</button>
          <button onClick={save} disabled={saving || !warehouseId}
            className="flex-1 bg-blue-600 text-white rounded-xl py-2 text-sm hover:bg-blue-700 disabled:opacity-50 font-medium">
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
        <p className="text-xs text-gray-400 text-center mt-3">⚠️ Ozon mutlak stok kullanır — girilen değer SET edilir</p>
      </div>
    </div>
  );
}

// ─── WHD İadeleri ─────────────────────────────────────────────────────────────

const WHD_STATUS_MAP = {
  ArrivedAtWarehouse: { label: 'WHD Depoda',    color: 'bg-blue-50 text-blue-700 border-blue-200' },
  ReadyForSale:       { label: 'Satışa Hazır',  color: 'bg-green-50 text-green-700 border-green-200' },
  Sold:               { label: 'Satıldı',        color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  WaitingForDecision: { label: 'Karar Bekliyor', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  ToBeUtilized:       { label: 'İmhaya Gidecek', color: 'bg-orange-50 text-orange-700 border-orange-200' },
  Cancelled:          { label: 'İptal',           color: 'bg-red-50 text-red-700 border-red-200' },
};

function fmtDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function WhdTab() {
  const [returns,  setReturns]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [lastFetch, setLastFetch] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('ozon_token');
      const r = await fetch('/api/ozon-stock/whd', { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || r.statusText);
      setReturns(d.returns || []);
      setLastFetch(new Date());
    } catch (e) { alert('Hata: ' + e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const statusCounts = useMemo(() => {
    const m = {};
    returns.forEach(r => { const k = r.visual?.status?.sys_name || 'unknown'; m[k] = (m[k] || 0) + 1; });
    return m;
  }, [returns]);

  const filtered = useMemo(() => returns.filter(r => {
    if (statusFilter && r.visual?.status?.sys_name !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return r.product?.offer_id?.toLowerCase().includes(q) ||
             r.product?.name?.toLowerCase().includes(q) ||
             r.posting_number?.toLowerCase().includes(q);
    }
    return true;
  }), [returns, search, statusFilter]);

  const totalValue = filtered.reduce((a, r) => a + parseFloat(r.product?.price?.price || 0), 0);

  return (
    <>
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <p className="text-sm text-gray-500">
          {returns.length} iade · ~${totalValue.toFixed(2)}
          {lastFetch && <span className="ml-2 text-gray-300">· {lastFetch.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>}
        </p>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-2 border border-gray-200 text-gray-600 px-3 py-1.5 rounded-xl text-sm font-medium disabled:opacity-50">
          <span className={loading ? 'animate-spin inline-block' : ''}>↻</span> Yenile
        </button>
      </div>

      {/* Durum chip'leri */}
      {!loading && (
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(statusCounts).map(([sys, cnt]) => {
            const m = WHD_STATUS_MAP[sys] || { label: sys, color: 'bg-gray-50 text-gray-600 border-gray-200' };
            return (
              <button key={sys} onClick={() => setStatusFilter(statusFilter === sys ? '' : sys)}
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-all ${m.color} ${statusFilter === sys ? 'ring-2 ring-offset-1 ring-current' : 'opacity-75 hover:opacity-100'}`}>
                {m.label} <span className="font-bold">{cnt}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex gap-2 mb-4">
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Offer ID, ürün adı veya sipariş no..."
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400 w-72" />
        {(search || statusFilter) && (
          <button onClick={() => { setSearch(''); setStatusFilter(''); }}
            className="text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2 px-2">Temizle</button>
        )}
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400 gap-3">
          <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
          <p className="text-sm">WHD iadeleri yükleniyor...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm">Sonuç bulunamadı</div>
      ) : (
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100 text-xs text-gray-500 font-semibold">
                <th className="text-left px-4 py-3">İade Tarihi</th>
                <th className="text-left px-4 py-3 min-w-[200px]">Ürün</th>
                <th className="text-left px-4 py-3">Sipariş No</th>
                <th className="text-left px-4 py-3">İade Nedeni</th>
                <th className="text-center px-4 py-3">Durum</th>
                <th className="text-center px-4 py-3">Fiyat</th>
                <th className="text-center px-4 py-3">Tazminat</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const sm = WHD_STATUS_MAP[r.visual?.status?.sys_name] || { label: r.visual?.status?.display_name || '—', color: 'bg-gray-50 text-gray-600 border-gray-200' };
                const compSys = r.compensation_status?.status?.sys_name;
                return (
                  <tr key={r.id} className="border-b border-gray-50 hover:bg-blue-50/20 transition-colors">
                    <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">{fmtDate(r.logistic?.return_date)}</td>
                    <td className="px-4 py-2.5">
                      <div className="font-mono text-xs text-gray-700">{r.product?.offer_id}</div>
                      <div className="text-xs text-gray-400 max-w-[240px] truncate mt-0.5" title={r.product?.name}>{r.product?.name}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <Link to={`/orders/${r.posting_number}`} className="font-mono text-xs text-blue-600 hover:underline whitespace-nowrap">
                        {r.posting_number}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-600 max-w-[180px]">{r.return_reason_name}</td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border ${sm.color}`}>{sm.label}</span>
                    </td>
                    <td className="px-4 py-2.5 text-center font-semibold text-gray-800 text-xs">
                      {r.product?.price?.price ? `$${r.product.price.price}` : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      {compSys === 'Received'
                        ? <span className="text-xs text-green-600 font-medium">✓ Alındı</span>
                        : compSys === 'Pending'
                        ? <span className="text-xs text-amber-600">Bekliyor</span>
                        : <span className="text-gray-300 text-xs">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="px-4 py-2 border-t border-gray-50 text-xs text-gray-400">
            {filtered.length} kayıt · Toplam ~${totalValue.toFixed(2)}
          </div>
        </div>
      )}
    </>
  );
}

// ─── İmha Tab ─────────────────────────────────────────────────────────────────

const IMHA_STATE_MAP = {
  OnWayToOzon:      { label: 'Ozon\'a Gidiyor',  color: 'bg-blue-50 text-blue-700 border-blue-200' },
  ArrivedAtWarehouse: { label: 'Depoda',          color: 'bg-amber-50 text-amber-700 border-amber-200' },
  Utilizing:        { label: 'İmha Sürecinde',    color: 'bg-orange-50 text-orange-700 border-orange-200' },
  Utilized:         { label: 'İmha Edildi',       color: 'bg-red-50 text-red-700 border-red-200' },
  UtilizedByOzon:   { label: 'Ozon İmha Etti',   color: 'bg-red-50 text-red-700 border-red-200' },
  MoneyReturned:    { label: 'Para İadesi',       color: 'bg-green-50 text-green-700 border-green-200' },
};

function ImhaTab() {
  const [returns,     setReturns]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [lastFetch,   setLastFetch]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('ozon_token');
      const r = await fetch('/api/ozon-stock/imha', { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || r.statusText);
      setReturns(d.returns || []);
      setLastFetch(new Date());
    } catch (e) { alert('Hata: ' + e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const stateCounts = useMemo(() => {
    const m = {};
    returns.forEach(r => { const k = r.state?.state || 'unknown'; m[k] = (m[k] || 0) + 1; });
    return m;
  }, [returns]);

  const filtered = useMemo(() => returns.filter(r => {
    if (stateFilter && r.state?.state !== stateFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return r.product?.offer_id?.toLowerCase().includes(q) ||
             r.product?.name?.toLowerCase().includes(q) ||
             r.posting_number?.toLowerCase().includes(q);
    }
    return true;
  }), [returns, search, stateFilter]);

  const totalValue = filtered.reduce((a, r) => a + parseFloat(r.product?.price || 0), 0);
  const activeCount = returns.filter(r => r.state?.state === 'Utilizing' || r.state?.state === 'ArrivedAtWarehouse' || r.state?.state === 'OnWayToOzon').length;

  return (
    <>
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <p className="text-sm text-gray-500">
          {returns.length} iade · <span className="text-orange-600 font-medium">{activeCount} aktif süreçte</span> · ~${totalValue.toFixed(2)}
          {lastFetch && <span className="ml-2 text-gray-300">· {lastFetch.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>}
        </p>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-2 border border-gray-200 text-gray-600 px-3 py-1.5 rounded-xl text-sm font-medium disabled:opacity-50">
          <span className={loading ? 'animate-spin inline-block' : ''}>↻</span> Yenile
        </button>
      </div>

      {/* Durum chip'leri */}
      {!loading && (
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(stateCounts).map(([state, cnt]) => {
            const m = IMHA_STATE_MAP[state] || { label: state, color: 'bg-gray-50 text-gray-600 border-gray-200' };
            return (
              <button key={state} onClick={() => setStateFilter(stateFilter === state ? '' : state)}
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-all ${m.color} ${stateFilter === state ? 'ring-2 ring-offset-1 ring-current' : 'opacity-75 hover:opacity-100'}`}>
                {m.label} <span className="font-bold">{cnt}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex gap-2 mb-4">
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Offer ID, ürün adı veya sipariş no..."
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400 w-72" />
        {(search || stateFilter) && (
          <button onClick={() => { setSearch(''); setStateFilter(''); }}
            className="text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2 px-2">Temizle</button>
        )}
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400 gap-3">
          <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
          <p className="text-sm">İmha iadeleri yükleniyor...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm">Sonuç bulunamadı</div>
      ) : (
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100 text-xs text-gray-500 font-semibold">
                <th className="text-left px-4 py-3">İade Tarihi</th>
                <th className="text-left px-4 py-3 min-w-[200px]">Ürün</th>
                <th className="text-left px-4 py-3">Sipariş No</th>
                <th className="text-center px-4 py-3">Durum</th>
                <th className="text-center px-4 py-3">Fiyat</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const sm = IMHA_STATE_MAP[r.state?.state] || { label: r.state?.state_name || '—', color: 'bg-gray-50 text-gray-600 border-gray-200' };
                const isActive = r.state?.state === 'Utilizing' || r.state?.state === 'ArrivedAtWarehouse' || r.state?.state === 'OnWayToOzon';
                return (
                  <tr key={r.return_id} className={`border-b border-gray-50 hover:bg-blue-50/20 transition-colors ${isActive ? 'bg-orange-50/20' : ''}`}>
                    <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                    <td className="px-4 py-2.5">
                      <div className="font-mono text-xs text-gray-700">{r.product?.offer_id}</div>
                      <div className="text-xs text-gray-400 max-w-[240px] truncate mt-0.5" title={r.product?.name}>{r.product?.name}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <Link to={`/orders/${r.posting_number}`} className="font-mono text-xs text-blue-600 hover:underline whitespace-nowrap">
                        {r.posting_number}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border ${sm.color}`}>{sm.label}</span>
                    </td>
                    <td className="px-4 py-2.5 text-center font-semibold text-gray-800 text-xs">
                      {r.product?.price ? `$${r.product.price}` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="px-4 py-2 border-t border-gray-50 text-xs text-gray-400">
            {filtered.length} kayıt · Toplam ~${totalValue.toFixed(2)}
          </div>
        </div>
      )}
    </>
  );
}

// ─── Stok Tab ─────────────────────────────────────────────────────────────────

const SORT_OPTIONS = [
  { value: 'offer_id_asc',  label: 'Offer ID (A→Z)' },
  { value: 'offer_id_desc', label: 'Offer ID (Z→A)' },
  { value: 'total_desc',    label: 'Toplam Stok (↓)' },
  { value: 'total_asc',     label: 'Toplam Stok (↑)' },
  { value: 'name_asc',      label: 'İsim (A→Z)' },
];

function StockTab() {
  const [products,   setProducts]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [sortBy,     setSortBy]     = useState('offer_id_asc');
  const [editModal,  setEditModal]  = useState(null);
  const [lastFetch,  setLastFetch]  = useState(null);
  const [types,      setTypes]      = useState([]);
  const [filterType, setFilterType] = useState('');
  const [page,       setPage]       = useState(1);
  const [whStocks,   setWhStocks]   = useState({});
  const PAGE_SIZE = 50;

  async function load() {
    setLoading(true);
    try {
      const raw = await api('GET', '/ozon-stock');
      api('GET', '/ozon-stock/all-warehouse-stocks').then(setWhStocks).catch(() => {});

      const typeSet = new Set();
      raw.forEach(item => { (item.stocks || []).forEach(s => { if (s.type) typeSet.add(s.type); }); });
      const typeList = [...typeSet].sort();
      setTypes(typeList);

      const processed = raw.map(item => {
        const stockMap = {};
        const reservedMap = {};
        const freeMap = {};
        let total = 0;
        (item.stocks || []).forEach(s => {
          const present  = s.present  || 0;
          const reserved = s.reserved || 0;
          const free     = Math.max(0, present - reserved);
          stockMap[s.type]    = (stockMap[s.type]    || 0) + present;
          reservedMap[s.type] = (reservedMap[s.type] || 0) + reserved;
          freeMap[s.type]     = (freeMap[s.type]     || 0) + free;
          total += free;
        });
        return { offer_id: item.offer_id, product_id: item.product_id, name: item.name || '', stocks: stockMap, reserved: reservedMap, free: freeMap, total };
      });

      setProducts(processed);
      setLastFetch(new Date());
      setPage(1);
    } catch (e) { alert('Hata: ' + e.message); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  function onUpdated(offerId, typeKey, newStock) {
    setProducts(prev => prev.map(p => {
      if (p.offer_id !== offerId) return p;
      const stocks = { ...p.stocks, [typeKey]: newStock };
      const total  = Object.values(stocks).reduce((a, b) => a + b, 0);
      return { ...p, stocks, total };
    }));
  }

  const filtered = useMemo(() => {
    let list = products;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p => p.offer_id.toLowerCase().includes(q) || p.name.toLowerCase().includes(q));
    }
    if (filterType) {
      list = list.filter(p => (p.stocks[filterType] || 0) > 0);
    }
    const [field, dir] = sortBy.split('_asc').length > 1
      ? [sortBy.replace('_asc', ''), 'asc']
      : [sortBy.replace('_desc', ''), 'desc'];

    list = [...list].sort((a, b) => {
      let va = field === 'total' ? a.total : (a[field] || '');
      let vb = field === 'total' ? b.total : (b[field] || '');
      if (typeof va === 'string') return dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return dir === 'asc' ? va - vb : vb - va;
    });
    return list;
  }, [products, search, sortBy, filterType]);

  const totalPages  = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated   = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalStock  = products.reduce((a, p) => a + p.total, 0);
  const zeroCount   = products.filter(p => p.total === 0).length;
  const lowCount    = products.filter(p => p.total > 0 && p.total < 5).length;

  function stockColor(n) {
    if (n === 0)  return 'text-gray-300 bg-gray-50';
    if (n < 5)    return 'text-orange-600 bg-orange-50 font-bold';
    return 'text-gray-800 bg-green-50';
  }

  return (
    <>
      {/* Başlık satırı */}
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div>
          <p className="text-sm text-gray-500 mt-0.5">
            {products.length} ürün · {totalStock.toLocaleString('tr-TR')} adet toplam
            {zeroCount > 0 && <span className="ml-2 text-gray-400">· <span className="text-gray-500">{zeroCount} sıfır stok</span></span>}
            {lowCount  > 0 && <span className="ml-2 text-orange-500">· {lowCount} kritik</span>}
            {lastFetch && <span className="ml-2 text-gray-300">· {lastFetch.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>}
          </p>
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-2 border border-gray-200 text-gray-600 hover:border-gray-300 px-3 py-1.5 rounded-xl text-sm font-medium transition-colors disabled:opacity-50">
          <span className={loading ? 'animate-spin inline-block' : ''}>↻</span> Yenile
        </button>
      </div>

      {/* Filtreler */}
      <div className="flex flex-wrap gap-2 mb-4">
        <input
          value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="Offer ID veya ürün adı ara..."
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400 w-64"
        />
        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400">
          {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {types.length > 1 && (
          <select value={filterType} onChange={e => { setFilterType(e.target.value); setPage(1); }}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400">
            <option value="">Tüm tipler</option>
            {types.map(t => <option key={t} value={t}>{t.toUpperCase()} stoklu</option>)}
          </select>
        )}
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400 gap-3">
          <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
          <p className="text-sm">Ozon'dan stok çekiliyor...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm">Sonuç bulunamadı</div>
      ) : (
        <>
          <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100 text-xs text-gray-500 font-semibold">
                  <th className="text-left px-4 py-3 min-w-[160px]">Offer ID</th>
                  <th className="text-left px-4 py-3 min-w-[200px]">Ürün Adı</th>
                  {types.map(t => (
                    <th key={t} className="text-center px-3 py-3 min-w-[100px]">
                      {t.toUpperCase()}
                      <span className="block text-gray-400 font-normal text-[10px]">sipariş edilebilir</span>
                    </th>
                  ))}
                  <th className="text-center px-4 py-3 min-w-[80px] text-gray-700">Toplam</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((p, idx) => (
                  <tr key={p.offer_id}
                    className={`border-b border-gray-50 hover:bg-blue-50/30 transition-colors ${idx % 2 === 0 ? '' : 'bg-gray-50/20'}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-mono text-xs text-gray-700">{p.offer_id}</div>
                      {whStocks[p.offer_id]?.length > 0 && (
                        <div className="text-[10px] text-blue-500 mt-0.5">
                          {whStocks[p.offer_id].map(w => `${w.warehouse_name} (${w.free_stock})`).join(' · ')}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500 max-w-[220px] truncate" title={p.name}>{p.name || '—'}</td>
                    {types.map(t => {
                      const free     = p.free[t]     ?? 0;
                      const reserved = p.reserved[t] ?? 0;
                      return (
                        <td key={t} className="px-3 py-2.5 text-center">
                          <button
                            onClick={() => setEditModal({ product: p, typeKey: t, typeName: t.toUpperCase(), currentStock: free })}
                            className={`inline-flex items-center justify-center min-w-[40px] px-2 py-1 rounded-lg text-xs transition-colors hover:ring-2 hover:ring-blue-300 cursor-pointer ${stockColor(free)}`}
                            title="Tıkla — stok güncelle (sipariş edilebilir)"
                          >
                            {free}
                          </button>
                          {reserved > 0 && <span className="block text-[10px] text-amber-500 mt-0.5">{reserved} rez.</span>}
                        </td>
                      );
                    })}
                    <td className="px-4 py-2.5 text-center font-bold text-gray-900 text-sm">{p.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Sayfalama */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-xs text-gray-400">
                {filtered.length} sonuçtan {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} gösteriliyor
              </p>
              <div className="flex gap-1">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm disabled:opacity-40 hover:bg-gray-50">‹</button>
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  const pg = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= totalPages - 3 ? totalPages - 6 + i : page - 3 + i;
                  return (
                    <button key={pg} onClick={() => setPage(pg)}
                      className={`px-3 py-1.5 border rounded-lg text-sm ${pg === page ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-200 hover:bg-gray-50'}`}>
                      {pg}
                    </button>
                  );
                })}
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm disabled:opacity-40 hover:bg-gray-50">›</button>
              </div>
            </div>
          )}

          <p className="text-xs text-gray-400 mt-3">
            Stoka tıkla → güncelle · Gösterilen değer sipariş edilebilir stok (rezerv hariç) · Turuncu = 5'ten az
          </p>
        </>
      )}

      {editModal && (
        <StockUpdateModal
          product={editModal.product}
          typeKey={editModal.typeKey}
          typeName={editModal.typeName}
          currentStock={editModal.currentStock}
          onClose={() => setEditModal(null)}
          onUpdated={onUpdated}
        />
      )}
    </>
  );
}

export default function OzonStock() {
  const [activeTab, setActiveTab] = useState('stock');
  return (
    <div className="p-4 md:p-6 max-w-full">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-xl font-bold text-gray-900">Ozon Stok</h1>
      </div>
      <div className="flex gap-1 mb-5 border-b border-gray-200">
        <button onClick={() => setActiveTab('stock')}
          className={`px-5 py-2.5 text-sm font-medium rounded-t-lg border-b-2 -mb-px transition-colors ${activeTab === 'stock' ? 'border-blue-600 text-blue-700 bg-blue-50' : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}>
          Mevcut Stok
        </button>
        <button onClick={() => setActiveTab('whd')}
          className={`px-5 py-2.5 text-sm font-medium rounded-t-lg border-b-2 -mb-px transition-colors ${activeTab === 'whd' ? 'border-blue-600 text-blue-700 bg-blue-50' : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}>
          WHD İadeleri
        </button>
        <button onClick={() => setActiveTab('imha')}
          className={`px-5 py-2.5 text-sm font-medium rounded-t-lg border-b-2 -mb-px transition-colors ${activeTab === 'imha' ? 'border-red-500 text-red-700 bg-red-50' : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}>
          İmha / Geri Dönüş
        </button>
      </div>
      {activeTab === 'stock' ? <StockTab /> : activeTab === 'whd' ? <WhdTab /> : <ImhaTab />}
    </div>
  );
}
